import * as THREE from "three";
import type { GraspCandidate } from "../types/grasps";

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function projectOntoPlane(v: THREE.Vector3, normalUnit: THREE.Vector3): THREE.Vector3 {
  return v.clone().sub(normalUnit.clone().multiplyScalar(v.dot(normalUnit)));
}

export function quatToMj(q: THREE.Quaternion): [number, number, number, number] {
  // [w, x, y, z]
  return [q.w, q.x, q.y, q.z];
}

export function quatAngleError(current: THREE.Quaternion, target: THREE.Quaternion): number {
  const qErr = target.clone().multiply(current.clone().invert()).normalize();
  const w = clamp(Math.abs(qErr.w), -1, 1);
  const angle = 2 * Math.acos(w);
  return Number.isFinite(angle) ? Math.min(Math.PI, Math.max(0, angle)) : Math.PI;
}

export function computeDesiredEndEffectorOrientationZFrame(args: {
  forwardLocal: THREE.Vector3;
  upLocal: THREE.Vector3;
  forwardWorld: THREE.Vector3;
  worldUp?: THREE.Vector3;
}): THREE.Quaternion | null {
  const { forwardLocal, upLocal, forwardWorld } = args;
  const worldUp = (args.worldUp || new THREE.Vector3(0, 0, 1)).clone().normalize();
  const fL = forwardLocal.clone().normalize();
  const uL = upLocal.clone().normalize();
  const fW = forwardWorld.clone().normalize();
  if (fL.lengthSq() < 1e-12 || uL.lengthSq() < 1e-12 || fW.lengthSq() < 1e-12) return null;

  const q1 = new THREE.Quaternion().setFromUnitVectors(fL, fW).normalize();
  const u1 = uL.clone().applyQuaternion(q1).normalize();
  let upDesired = projectOntoPlane(worldUp, fW);
  if (upDesired.lengthSq() < 1e-10) upDesired = projectOntoPlane(new THREE.Vector3(1, 0, 0), fW);
  upDesired.normalize();
  let u1Proj = projectOntoPlane(u1, fW);
  if (u1Proj.lengthSq() < 1e-10) u1Proj = projectOntoPlane(new THREE.Vector3(0, 1, 0), fW);
  u1Proj.normalize();
  const cross = u1Proj.clone().cross(upDesired);
  const sin = fW.dot(cross);
  const cos = clamp(u1Proj.dot(upDesired), -1, 1);
  const roll = Math.atan2(sin, cos);
  const q2 = new THREE.Quaternion().setFromAxisAngle(fW, roll).normalize();
  return q2.clone().multiply(q1).normalize();
}

export function pickNearestSymmetricRollOrientation(args: {
  desired: THREE.Quaternion;
  current: THREE.Quaternion;
  axisWorld: THREE.Vector3;
  symmetryTurns?: number;
}): THREE.Quaternion {
  const { desired, current } = args;
  const axisWorld = args.axisWorld.clone().normalize();
  const symmetryTurns = Math.max(1, Math.floor(args.symmetryTurns ?? 1));
  if (axisWorld.lengthSq() < 1e-12 || symmetryTurns <= 1) return desired.clone().normalize();

  let best = desired.clone().normalize();
  let bestErr = quatAngleError(current, best);
  for (let i = 1; i < symmetryTurns; i += 1) {
    const roll = (2 * Math.PI * i) / symmetryTurns;
    const qRoll = new THREE.Quaternion().setFromAxisAngle(axisWorld, roll).normalize();
    const candidate = qRoll.multiply(desired).normalize();
    const err = quatAngleError(current, candidate);
    if (err < bestErr) {
      best = candidate;
      bestErr = err;
    }
  }
  return best;
}

export function resolveKeepDownLocalAxes(
  targetOffset: {
    forwardLocalZFrame?: THREE.Vector3;
    upLocalZFrame?: THREE.Vector3;
  } | null
): { forwardLocal: THREE.Vector3; upLocal: THREE.Vector3 } {
  const forwardLocal =
    targetOffset?.forwardLocalZFrame instanceof THREE.Vector3
      ? targetOffset.forwardLocalZFrame.clone().normalize()
      : new THREE.Vector3(0, 0, 1);
  const upLocal =
    targetOffset?.upLocalZFrame instanceof THREE.Vector3
      ? targetOffset.upLocalZFrame.clone().normalize()
      : new THREE.Vector3(0, 1, 0);
  return { forwardLocal, upLocal };
}

export type RankedGraspWorldZ = {
  orientationZ: THREE.Quaternion;
  sourceIndex: number;
  combinedScore: number;
  topDownScore: number;
  distanceScore: number;
};

export function rankGraspOrientationsWorldZUp(args: {
  grasps: GraspCandidate[];
  bodyWorldQuatScene: THREE.Quaternion;
  bodyWorldPosScene: THREE.Vector3;
  yToZ: THREE.Quaternion;
  eeWorldPosScene?: THREE.Vector3 | null;
  rollOffsetRad?: number;
}): RankedGraspWorldZ[] {
  const {
    grasps,
    bodyWorldQuatScene,
    bodyWorldPosScene,
    yToZ,
    eeWorldPosScene = null,
    rollOffsetRad = Math.PI / 2,
  } = args;
  if (!Array.isArray(grasps) || grasps.length === 0) return [];
  const worldDownScene = new THREE.Vector3(0, -1, 0);
  const rollOffset = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 0, 1),
    rollOffsetRad
  );
  const ranked: RankedGraspWorldZ[] = [];
  for (let sourceIndex = 0; sourceIndex < grasps.length; sourceIndex += 1) {
    const grasp = grasps[sourceIndex];
    const localPosZUp = new THREE.Vector3(...grasp.position);
    const worldPosScene = localPosZUp
      .clone()
      .applyQuaternion(bodyWorldQuatScene)
      .add(bodyWorldPosScene);
    const [qw, qx, qy, qz] = grasp.orientation;
    const rawQuat = new THREE.Quaternion(qx, qy, qz, qw);
    const graspLocalQuatZUp = rawQuat.clone().multiply(rollOffset);
    const graspWorldQuatScene = bodyWorldQuatScene.clone().multiply(graspLocalQuatZUp);
    const graspWorldQuatZ = yToZ.clone().multiply(graspWorldQuatScene);
    const approachWorldScene = new THREE.Vector3(0, 0, 1)
      .applyQuaternion(graspWorldQuatScene)
      .normalize();
    const topDownScore = Math.max(0, Math.min(1, (approachWorldScene.dot(worldDownScene) + 1) / 2));
    const distanceScore =
      eeWorldPosScene instanceof THREE.Vector3
        ? 1 / (1 + worldPosScene.distanceTo(eeWorldPosScene))
        : 0.5;
    const combinedScore = topDownScore * 0.8 + distanceScore * 0.2;
    ranked.push({
      orientationZ: graspWorldQuatZ,
      sourceIndex,
      combinedScore,
      topDownScore,
      distanceScore,
    });
  }
  ranked.sort((a, b) => b.combinedScore - a.combinedScore);
  return ranked;
}

export function buildKeepDownOrientationTargetZFrame(args: {
  currentRotationZ: THREE.Quaternion;
  forwardLocal: THREE.Vector3;
  upLocal: THREE.Vector3;
  forwardWorldZ?: THREE.Vector3;
  worldUpZ?: THREE.Vector3;
  symmetryTurns?: number;
}): THREE.Quaternion {
  const forwardWorld = args.forwardWorldZ || new THREE.Vector3(0, 0, -1);
  const worldUp = args.worldUpZ || new THREE.Vector3(0, 1, 0);
  const desiredBase =
    computeDesiredEndEffectorOrientationZFrame({
      forwardLocal: args.forwardLocal,
      upLocal: args.upLocal,
      forwardWorld,
      worldUp,
    }) || args.currentRotationZ.clone();
  return pickNearestSymmetricRollOrientation({
    desired: desiredBase,
    current: args.currentRotationZ,
    axisWorld: forwardWorld.clone(),
    symmetryTurns: Math.max(1, Math.floor(args.symmetryTurns ?? 4)),
  });
}
